// frontend/src/pages/AdminDashboard.js – NextScenes Admin Dashboard
// Build: 2026-05-12-admin-dashboard-public-homepage-link-v1
//
// Keeps existing admin dashboard logic:
// - Status/stats load from /api/admin/dashboard/*
// - Users list loads from canonical admin users API: GET /api/admin/users
// - Home Spotlight editing remains unchanged in purpose and endpoint use
// - Adds clear navigation to the internal Writer Reviews matrix
// - Adds clear navigation to the Public Homepage Manager

import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import { api } from "../api";

/* ------------------ tiny UI helpers ------------------ */

function Card({ title, value, sub, tone }) {
  const cls = "ns-admin-stat-card" + (tone ? ` is-${tone}` : "");
  return (
    <div className={cls}>
      <div className="ns-admin-stat-title">{title}</div>
      <div className="ns-admin-stat-value">{value}</div>
      {sub ? <div className="ns-admin-stat-sub">{sub}</div> : null}
    </div>
  );
}

function SmallButton({ children, onClick, disabled, variant }) {
  const isPrimary = variant === "primary";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={isPrimary ? "ns-button ns-button-primary" : "ns-button ns-button-outline"}
      style={{ padding: "8px 12px", borderRadius: 999, fontWeight: 700, fontSize: 13 }}
    >
      {children}
    </button>
  );
}

function Pill({ children }) {
  return <span className="ns-admin-pill">{children}</span>;
}

function fmtBool(v) {
  return v ? "Yes" : "No";
}

function roleLabel(role) {
  const r = String(role || "").toLowerCase();
  if (r === "admin") return "admin";
  if (r === "moderator") return "moderator";
  if (r === "writer" || r === "power") return "writer";
  return "user";
}

function n(v) {
  const num = Number(v);
  return Number.isFinite(num) ? num : 0;
}

function asArray(value, keys = []) {
  if (Array.isArray(value)) return value;
  for (const key of keys) {
    if (Array.isArray(value?.[key])) return value[key];
  }
  return [];
}

function shortDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

function shortText(value, max = 88) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max).trim()}…` : text;
}

function TextInput({ label, value, onChange, placeholder }) {
  return (
    <div className="ns-admin-field">
      <label>{label}</label>
      <input
        className="ns-input"
        value={value}
        placeholder={placeholder || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function TextArea({ label, value, onChange, placeholder, rows = 5 }) {
  return (
    <div className="ns-admin-field">
      <label>{label}</label>
      <textarea
        value={value}
        placeholder={placeholder || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
      />
    </div>
  );
}

/* ------------------ API helpers ------------------ */

async function apiGet(path) {
  if (api && typeof api.get === "function") return await api.get(path);
  return await api(path, { method: "GET" });
}

async function apiPut(path, body) {
  if (api && typeof api.put === "function") return await api.put(path, body);
  return await api(path, { method: "PUT", body: JSON.stringify(body) });
}

/* ------------------ main component ------------------ */

export default function AdminDashboard({ onGo }) {
  const [tab, setTab] = useState("dashboard"); // dashboard | users | content | system | spotlight
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  // Backend admin dashboard (read-only status/stats)
  const BASE = "/api/admin/dashboard";

  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState(null);

  // Canonical admin users list (ID-based management API)
  const [users, setUsers] = useState([]);

  // Dashboard briefing uses existing admin endpoints only.
  // It is deliberately read-only: it surfaces attention, but does not create a new audit system.
  const [briefing, setBriefing] = useState({
    pendingWriterRequests: [],
    contactStats: null,
    recentContacts: [],
    betaItems: [],
    loadedAt: "",
  });

  // Home Spotlight
  const [spotlight, setSpotlight] = useState(null);
  const [spotDraft, setSpotDraft] = useState(null);
  const [dirty, setDirty] = useState(false);

  const sys = useMemo(() => {
    const s = status || {};
    return {
      apiOnline: Boolean(s.ok),
      environment: String(s.env || "").toLowerCase() || (s.isProd ? "production" : "development"),
      isProd: s.isProd === true,
      mongoReadyState: s.mongoReadyState ?? 0,
      mongoConnected: s.mongoConnected === true || s.mongoReadyState === 1,
      build: s.build || "unknown",
      time: s.time || "",
    };
  }, [status]);

  const userStats = useMemo(() => {
    const st = stats?.users || {};
    return {
      total: Number(st.total || 0),
      active: Number(st.active || 0),
      admins: Number(st.admins || 0),
      pendingEmailVerifications: Number(st.pendingEmailVerifications || 0),
    };
  }, [stats]);

  const contentStats = useMemo(() => {
    const c = stats?.content || {};
    return {
      storylines: c.storylines ?? "unknown",
      comments: c.comments ?? "unknown",
      pendingWriterRequests: n(c.pendingWriterRequests),
    };
  }, [stats]);

  function clearFlash() {
    setErr("");
    setMsg("");
  }

  async function refreshAll() {
    clearFlash();
    setBusy(true);
    try {
      const [st, st2, u] = await Promise.all([
        apiGet(`${BASE}/status`),
        apiGet(`${BASE}/stats`),
        apiGet("/api/admin/users"),
      ]);

      const userList = u && Array.isArray(u.users) ? u.users : asArray(u, ["items"]);

      setStatus(st);
      setStats(st2);
      setUsers(userList);

      const [writerReqRes, contactStatsRes, contactListRes, betaRes] = await Promise.allSettled([
        apiGet("/api/admin/writer-requests?status=pending"),
        apiGet("/api/admin/contact/stats"),
        apiGet("/api/admin/contact?limit=5"),
        apiGet("/api/feedback/live?target=beta&limit=20"),
      ]);

      const writerReq = writerReqRes.status === "fulfilled" ? writerReqRes.value : null;
      const contactStats = contactStatsRes.status === "fulfilled" ? contactStatsRes.value : null;
      const contactList = contactListRes.status === "fulfilled" ? contactListRes.value : null;
      const beta = betaRes.status === "fulfilled" ? betaRes.value : null;

      setBriefing({
        pendingWriterRequests: asArray(writerReq, ["requests", "items"]),
        contactStats: contactStats?.ok ? contactStats : null,
        recentContacts: asArray(contactList, ["items"]),
        betaItems: asArray(beta, ["items"]).filter((item) => !item?.parentId),
        loadedAt: new Date().toISOString(),
      });
    } catch (e) {
      const m = String(e?.data?.message || e?.message || "Failed to refresh admin dashboard.");
      setErr(m);
    } finally {
      setBusy(false);
    }
  }

  async function loadSpotlight() {
    clearFlash();
    setBusy(true);
    try {
      const res = await apiGet("/api/home/spotlight/editor");
      const sp = res?.spotlight || null;
      setSpotlight(sp);
      setSpotDraft(sp ? JSON.parse(JSON.stringify(sp)) : null);
      setDirty(false);
    } catch (e) {
      const m = String(e?.data?.message || e?.message || "Failed to load Home Spotlight.");
      setErr(m);
    } finally {
      setBusy(false);
    }
  }

  async function saveSpotlight() {
    clearFlash();
    if (!spotDraft) return;

    setBusy(true);
    try {
      const payload = {
        writer: spotDraft.writer || {},
        book: spotDraft.book || {},
      };

      const res = await apiPut("/api/home/spotlight", payload);
      const sp = res?.spotlight || null;

      setSpotlight(sp);
      setSpotDraft(sp ? JSON.parse(JSON.stringify(sp)) : null);
      setDirty(false);
      setMsg("Saved.");
    } catch (e) {
      const m = String(e?.data?.message || e?.message || "Failed to save Home Spotlight.");
      setErr(m);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === "spotlight" && !spotlight && !busy) {
      loadSpotlight();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  function leftItem(id, label) {
    const active = tab === id;
    return (
      <button
        type="button"
        onClick={() => {
          clearFlash();
          setTab(id);
        }}
        className={active ? "ns-admin-side-tab is-active" : "ns-admin-side-tab"}
      >
        {label}
      </button>
    );
  }

  function sideAction(label, onClick, emphasis = false) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={emphasis ? "ns-admin-side-action is-emphasis" : "ns-admin-side-action"}
      >
        {label}
      </button>
    );
  }

  function go(destination) {
    if (typeof onGo === "function") onGo(destination);
  }

  function attentionItems() {
    const contactNew = n(briefing.contactStats?.byStatus?.new);
    const contactActive = n(briefing.contactStats?.active);
    const writerRequests = Math.max(
      n(contentStats.pendingWriterRequests),
      Array.isArray(briefing.pendingWriterRequests) ? briefing.pendingWriterRequests.length : 0
    );
    const betaTopLevel = Array.isArray(briefing.betaItems) ? briefing.betaItems.length : 0;

    return [
      {
        label: "Writer requests",
        value: writerRequests,
        detail: "Applicants waiting for a role decision.",
        action: "Open requests",
        onClick: () => go("adminPanel"),
      },
      {
        label: "Email verifications",
        value: userStats.pendingEmailVerifications,
        detail: "Active accounts still awaiting verification.",
        action: "Open users",
        onClick: () => go("adminUsers"),
      },
      {
        label: "Contact messages",
        value: contactNew,
        detail: contactActive ? `${contactActive} active message${contactActive === 1 ? "" : "s"} in the inbox.` : "No active contact messages.",
        action: "Open inbox",
        onClick: () => go("adminContactInbox"),
      },
      {
        label: "Beta feedback",
        value: betaTopLevel,
        detail: "Recent top-level beta notes from testers and members.",
        action: "Open feedback",
        onClick: () => go("adminBetaFeedback"),
      },
    ];
  }

  function recentSignals() {
    const out = [];

    for (const u of (users || []).slice(0, 4)) {
      out.push({
        type: "User",
        title: u?.name || u?.username || u?.email || "New account",
        detail: roleLabel(u?.role),
        date: u?.createdAt || u?.updatedAt,
      });
    }

    for (const r of (briefing.pendingWriterRequests || []).slice(0, 4)) {
      out.push({
        type: "Writer request",
        title: r?.user?.name || r?.user?.username || r?.user?.email || "Pending request",
        detail: shortText(r?.message || "Awaiting review", 90),
        date: r?.updatedAt || r?.createdAt,
      });
    }

    for (const m of (briefing.recentContacts || []).slice(0, 4)) {
      out.push({
        type: "Contact",
        title: m?.subject || m?.name || "Contact message",
        detail: shortText(m?.message || m?.email || "Message received", 90),
        date: m?.createdAt,
      });
    }

    for (const b of (briefing.betaItems || []).slice(0, 4)) {
      out.push({
        type: "Beta",
        title: b?.author || "Beta feedback",
        detail: shortText(b?.text || "Feedback received", 90),
        date: b?.createdAt,
      });
    }

    return out
      .filter((x) => x.date)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 6);
  }

  function renderPendingAttention() {
    const items = attentionItems();
    const total = items.reduce((sum, item) => sum + n(item.value), 0);

    return (
      <section className="ns-admin-briefing-card">
        <div className="ns-admin-briefing-head">
          <div>
            <div className="ns-admin-mini-title">Pending Attention</div>
            <h3>Morning briefing</h3>
            <p>Items that may need an admin decision, reply, or review.</p>
          </div>
          <Pill>{total > 0 ? `${total} item${total === 1 ? "" : "s"}` : "Clear"}</Pill>
        </div>

        <div className="ns-admin-attention-grid">
          {items.map((item) => (
            <div className={item.value > 0 ? "ns-admin-attention-item" : "ns-admin-attention-item is-clear"} key={item.label}>
              <div className="ns-admin-attention-top">
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
              <p>{item.detail}</p>
              <button type="button" onClick={item.onClick}>
                {item.action}
              </button>
            </div>
          ))}
        </div>
      </section>
    );
  }

  function renderRecentSignals() {
    const signals = recentSignals();

    return (
      <section className="ns-admin-ledger-card">
        <div className="ns-admin-briefing-head">
          <div>
            <div className="ns-admin-mini-title">Recent Signals</div>
            <h3>What changed around the gates</h3>
            <p>This is based on available records. A full audit log can be added later.</p>
          </div>
          {briefing.loadedAt ? <Pill>Updated {shortDate(briefing.loadedAt)}</Pill> : null}
        </div>

        {signals.length === 0 ? (
          <div className="ns-admin-empty">No recent signals found.</div>
        ) : (
          <div className="ns-admin-ledger-list">
            {signals.map((item, index) => (
              <div className="ns-admin-ledger-row" key={`${item.type}_${item.date}_${index}`}>
                <div>
                  <span>{item.type}</span>
                  <strong>{item.title}</strong>
                  {item.detail ? <p>{item.detail}</p> : null}
                </div>
                <time>{shortDate(item.date)}</time>
              </div>
            ))}
          </div>
        )}
      </section>
    );
  }

  function renderHeader() {
    const title =
      tab === "dashboard"
        ? "Stewardship Console"
        : tab === "users"
        ? "People"
        : tab === "content"
        ? "Stories and Conversations"
        : tab === "system"
        ? "Technical Health"
        : "Home Spotlight";

    const subtitle =
      tab === "dashboard"
        ? "A calm command room for platform health, people, stories, and public presentation."
        : tab === "users"
        ? "A readable account overview. Detailed actions remain in User Management."
        : tab === "content"
        ? "A quick view of platform writing activity and participation."
        : tab === "system"
        ? "Service, build, and database status without unnecessary noise."
        : "Edit the three public Home page highlights with care.";

    return (
      <div className="ns-admin-section-head">
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>

        <div className="ns-admin-section-actions">
          {tab === "spotlight" ? (
            <>
              <SmallButton onClick={loadSpotlight} disabled={busy} variant="outline">
                {busy ? "Loading..." : "Reload"}
              </SmallButton>
              <SmallButton onClick={saveSpotlight} disabled={busy || !dirty} variant="primary">
                {busy ? "Saving..." : dirty ? "Save" : "Saved"}
              </SmallButton>
            </>
          ) : (
            <SmallButton onClick={refreshAll} disabled={busy} variant="outline">
              {busy ? "Refreshing..." : "Refresh"}
            </SmallButton>
          )}
        </div>
      </div>
    );
  }

  function renderDashboard() {
    return (
      <>
        <div className="ns-admin-mini-title">System Status</div>

        <div className="ns-admin-stat-grid four">
          <Card title="API" value={sys.apiOnline ? "Online" : "Offline"} tone="health" />
          <Card title="Database" value={sys.mongoConnected ? "Connected" : "Not Connected"} tone="database" />
          <Card title="Environment" value={sys.environment || "unknown"} tone="environment" />
          <Card title="Build" value={String(sys.build || "unknown")} tone="build" />
        </div>

        <div className="ns-admin-spacer" />

        <div className="ns-admin-mini-title">Platform Snapshot</div>

        <div className="ns-admin-stat-grid three">
          <Card title="Total Users" value={userStats.total} tone="people" />
          <Card title="Active Users" value={userStats.active} tone="health" />
          <Card title="Admin Users" value={userStats.admins} tone="stewards" />
          <Card title="Pending Verifications" value={userStats.pendingEmailVerifications} tone="attention" />
          <Card title="Writer Requests" value={String(contentStats.pendingWriterRequests)} tone="attention" />
          <Card title="Storylines" value={String(contentStats.storylines)} sub="Includes archived/private" tone="stories" />
          <Card title="Comments" value={String(contentStats.comments)} tone="conversation" />
        </div>

        <div className="ns-admin-spacer" />

        {renderPendingAttention()}

        <div className="ns-admin-spacer" />

        {renderRecentSignals()}

        <div className="ns-admin-spacer" />

        <div className="ns-admin-tool-callout">
          <div>
            <div className="ns-admin-tool-kicker">Private review</div>
            <h3>Founding Writers Review Matrix</h3>
            <p>
              Review eligible storylines privately for promotion, visibility, and future recognition consideration.
            </p>
          </div>
          <button
            type="button"
            className="ns-button ns-button-primary"
            onClick={() => onGo && onGo("adminWriterReviews")}
          >
            Open Writer Reviews
          </button>
        </div>

        <div className="ns-admin-note-box">
          Use the sidebar as the working map: <b>Public Homepage</b>, <b>User Management</b>, <b>Writer Requests</b>,
          <b> Storylines</b>, <b>Writer Reviews</b>, <b>Contact Inbox</b>, and <b>Beta Feedback</b>.
        </div>
      </>
    );
  }

  function renderUsers() {
    const list = Array.isArray(users) ? users : [];

    return (
      <>
        <div className="ns-admin-pill-row">
          <Pill>Total: {list.length}</Pill>
          <Pill>Active: {userStats.active}</Pill>
          <Pill>Admins: {userStats.admins}</Pill>
          <Pill>Pending verify: {userStats.pendingEmailVerifications}</Pill>
        </div>

        <div className="ns-admin-table-wrap">
          <div className="ns-admin-users-head">
            <div>Email</div>
            <div>Username</div>
            <div>Name</div>
            <div>Role</div>
            <div>Status</div>
          </div>

          {list.length === 0 ? (
            <div className="ns-admin-empty">No users found.</div>
          ) : (
            list.map((u, index) => {
              const key = String(u?.id || `${u?.email || "user"}_${index}`);
              const active = u?.isActive === false ? "Disabled" : "Active";

              return (
                <div className="ns-admin-users-row" key={key}>
                  <div className="strong">
                    {u?.email || "(no email)"}
                    <div className="muted">Verified: {fmtBool(u?.emailVerified)}</div>
                  </div>
                  <div className="strong">{u?.username || ""}</div>
                  <div>{u?.name || ""}</div>
                  <div><Pill>{roleLabel(u?.role)}</Pill></div>
                  <div><Pill>{active}</Pill></div>
                </div>
              );
            })
          )}
        </div>

        <div className="ns-admin-help-text">
          This table is read-only. For actions, use <b>User Management (Actions)</b> in the sidebar.
        </div>
      </>
    );
  }

  function renderContent() {
    const s = contentStats.storylines;
    const c = contentStats.comments;

    return (
      <>
        <div className="ns-admin-stat-grid three">
          <Card title="Storylines" value={String(s)} sub="Includes archived/private" tone="stories" />
          <Card title="Writer Requests" value={String(contentStats.pendingWriterRequests)} sub="Pending" tone="attention" />
          <Card title="Comments" value={String(c)} tone="conversation" />
          <Card title="Users" value={String(userStats.total)} tone="people" />
        </div>

        <div className="ns-admin-help-text">
          Note: the public site often lists only <b>active</b> and <b>public</b> storylines. That is why totals can differ.
        </div>
      </>
    );
  }

  function renderSystem() {
    const safe = {
      ok: status?.ok,
      service: status?.service,
      build: status?.build,
      env: status?.env,
      isProd: status?.isProd,
      time: status?.time,
      mongoReadyState: status?.mongoReadyState,
      mongoConnected: status?.mongoConnected,
    };

    return (
      <>
        <div className="ns-admin-pill-row">
          <Pill>Build: {String(sys.build || "unknown")}</Pill>
          <Pill>Env: {String(sys.environment || "unknown")}</Pill>
          <Pill>DB Connected: {fmtBool(sys.mongoConnected)}</Pill>
          <Pill>ReadyState: {String(sys.mongoReadyState)}</Pill>
        </div>

        <div className="ns-admin-code-card">
          <div className="ns-admin-mini-title">Admin Status</div>
          <pre>{JSON.stringify(safe, null, 2)}</pre>
        </div>
      </>
    );
  }

  function setDraft(path, value) {
    setSpotDraft((prev) => {
      const cur = prev ? JSON.parse(JSON.stringify(prev)) : {};
      const parts = String(path || "").split(".");
      let obj = cur;

      for (let i = 0; i < parts.length - 1; i += 1) {
        const k = parts[i];
        if (!obj[k] || typeof obj[k] !== "object") obj[k] = {};
        obj = obj[k];
      }
      obj[parts[parts.length - 1]] = value;
      return cur;
    });
    setDirty(true);
  }

  function renderSpotlight() {
    const sp = spotDraft || spotlight;

    if (!sp) {
      return <div className="ns-admin-empty">Loading spotlight…</div>;
    }

    const w = sp.writer || {};
    const b = sp.book || {};

    return (
      <div className="ns-admin-spotlight-wrap">
        <div className="banner" style={{ margin: 0 }}>
          This edits Writer and Book Spotlight. Daily Words are managed exclusively through Content Steward → Editorial Calendar.
        </div>

        <div className="ns-admin-spotlight-grid">
          <div className="card ns-admin-spotlight-card">
            <div className="ns-admin-mini-title">Writer of the Month</div>
            <div className="ns-admin-form-grid">
              <TextInput label="Name" value={w.name || ""} onChange={(v) => setDraft("writer.name", v)} />
              <TextInput
                label="Month Label"
                value={w.monthLabel || ""}
                onChange={(v) => setDraft("writer.monthLabel", v)}
                placeholder="January 2026"
              />
              <TextInput
                label="Image URL"
                value={w.imageUrl || ""}
                onChange={(v) => setDraft("writer.imageUrl", v)}
                placeholder="/writers/wole-soyinka.jpg or https://..."
              />
              <TextArea label="Teaser (English)" value={w.teaser || ""} onChange={(v) => setDraft("writer.teaser", v)} rows={4} />
              <TextArea label="Teaser (French)" value={w.teaserFr || ""} onChange={(v) => setDraft("writer.teaserFr", v)} rows={4} />
              <TextArea label="Full Text (English)" value={w.fullText || ""} onChange={(v) => setDraft("writer.fullText", v)} rows={7} />
              <TextArea label="Full Text (French)" value={w.fullTextFr || ""} onChange={(v) => setDraft("writer.fullTextFr", v)} rows={7} />
              <TextArea
                label="Selected Works (one per line)"
                value={Array.isArray(w.works) ? w.works.join("\n") : ""}
                onChange={(v) =>
                  setDraft(
                    "writer.works",
                    String(v || "")
                      .split("\n")
                      .map((x) => x.trim())
                      .filter(Boolean)
                  )
                }
                rows={5}
              />
            </div>
          </div>

          <div className="card ns-admin-spotlight-card">
            <div className="ns-admin-mini-title">Book of the Month</div>
            <div className="ns-admin-form-grid">
              <TextInput label="Title (English)" value={b.title || ""} onChange={(v) => setDraft("book.title", v)} />
              <TextInput label="Title (French)" value={b.titleFr || ""} onChange={(v) => setDraft("book.titleFr", v)} />
              <TextInput label="Author" value={b.author || ""} onChange={(v) => setDraft("book.author", v)} />
              <TextInput
                label="Cover URL (English)"
                value={b.coverUrl || ""}
                onChange={(v) => setDraft("book.coverUrl", v)}
                placeholder="/assets/so-long-a-letter.png or https://..."
              />
              <TextInput
                label="Cover URL (French)"
                value={b.coverUrlFr || ""}
                onChange={(v) => setDraft("book.coverUrlFr", v)}
                placeholder="/assets/une-si-longue-lettre.png or https://..."
              />
              <TextArea label="Teaser (English)" value={b.teaser || ""} onChange={(v) => setDraft("book.teaser", v)} rows={6} />
              <TextArea label="Teaser (French)" value={b.teaserFr || ""} onChange={(v) => setDraft("book.teaserFr", v)} rows={6} />
              <TextArea label="Full Text (English, optional)" value={b.fullText || ""} onChange={(v) => setDraft("book.fullText", v)} rows={6} />
              <TextArea label="Full Text (French, optional)" value={b.fullTextFr || ""} onChange={(v) => setDraft("book.fullTextFr", v)} rows={6} />
              <TextInput
                label="Link URL"
                value={b.linkUrl || ""}
                onChange={(v) => setDraft("book.linkUrl", v)}
                placeholder="https://... or /storylines"
              />
            </div>
          </div>

        </div>
      </div>
    );
  }

  return (
    <div className="page ns-admin-dashboard-page">
      <section className="ns-admin-hero">
        <div>
          <div className="ns-admin-hero-kicker">NextScenes Stewardship</div>
          <h1>Admin Dashboard</h1>
          <p>Oversee the platform with order, restraint, and clarity. The public rooms must remain welcoming; the control room must remain calm.</p>
        </div>
        <div className="ns-admin-hero-actions">
          <button type="button" className="ns-button ns-button-outline" onClick={() => onGo && onGo("adminPublicHomepage")}>
            Public Homepage
          </button>
          <button type="button" className="ns-button ns-button-outline" onClick={() => onGo && onGo("adminWriterReviews")}>
            Writer Reviews
          </button>
          <button type="button" className="ns-button ns-button-primary" onClick={refreshAll} disabled={busy}>
            {busy ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </section>

      <div className="ns-admin-dashboard-shell">
        <aside className="ns-admin-sidebar">
          <div className="ns-admin-sidebar-brand">CONTROL ROOM</div>

          <div className="ns-admin-sidebar-group">
            {leftItem("dashboard", "Dashboard")}
            {leftItem("users", "Users")}
            {leftItem("content", "Content")}
            {leftItem("system", "System")}
            {leftItem("spotlight", "Home Spotlight")}
          </div>

          <div className="ns-admin-sidebar-heading">Admin Actions</div>

          <div className="ns-admin-sidebar-group">
            {sideAction("Public Homepage", () => onGo && onGo("adminPublicHomepage"), true)}
            {sideAction("Writer Reviews", () => onGo && onGo("adminWriterReviews"))}
            {sideAction("User Management", () => onGo && onGo("adminUsers"))}
            {sideAction("Writer Requests", () => onGo && onGo("adminPanel"))}
            {sideAction("Storylines", () => onGo && onGo("adminStories"))}
            {sideAction("Contact Inbox", () => onGo && onGo("adminContactInbox"))}
            {sideAction("Beta Feedback", () => onGo && onGo("adminBetaFeedback"))}
          </div>

          <div className="ns-admin-sidebar-heading">Admin Corner</div>
          <div className="ns-admin-sidebar-note">
            Keep actions deliberate. Internal review scores remain private to NextScenes.
          </div>

          {typeof onGo === "function" ? (
            <div className="ns-admin-sidebar-group">
              <SmallButton onClick={() => onGo("home")} disabled={busy} variant="outline">
                Go Home
              </SmallButton>
              <SmallButton onClick={() => onGo("logout")} disabled={busy} variant="outline">
                Log out
              </SmallButton>
            </div>
          ) : null}
        </aside>

        <main className="ns-admin-main-panel">
          <div className="ns-admin-panel-topline">
            <span>ADMIN OFFICE</span>
            {busy ? <Pill>Working…</Pill> : dirty && tab === "spotlight" ? <Pill>Unsaved</Pill> : <Pill>Ready</Pill>}
          </div>

          {err ? <div className="banner ns-admin-flash">{err}</div> : null}
          {msg ? <div className="banner ns-admin-flash is-ok">{msg}</div> : null}

          {renderHeader()}

          {tab === "dashboard" ? renderDashboard() : null}
          {tab === "users" ? renderUsers() : null}
          {tab === "content" ? renderContent() : null}
          {tab === "system" ? renderSystem() : null}
          {tab === "spotlight" ? renderSpotlight() : null}
        </main>
      </div>
    </div>
  );
}
